import { useEffect, useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ENTITY_PREFIX, Roles, TREATMENT_PLAN_V3_VERSION } from '../../../../constants';
import { FeatureFlags } from '../../../../constants/featureFlags';
import { areUserRolesIn } from '../../../../helpers';
import { LOOKUP_SESSION_STORAGE } from '../../../../i18n';
import { SESSION_NOTE_VERSIONS } from '../../../../pages/session/btSessionNotes/constants';
import { providerActions, sessionActions } from '../../../../store';
import { getTreatmentPlanStatus } from '../../treatment-plan/utils';
import { EMPTY_HEADER_METADATA } from '../constants';
import {
  getHeaderAuthorName,
  getHeaderInvoiceStatusLabel,
  getHeaderPageTitle,
  getHeaderSessionStatusLabel,
  getHeaderTitleForPath,
  getQuestionnaireHeaderMetadata,
} from '../utils';
import { useSelectedClientActiveTreatmentPlanId } from './useSelectedClientActiveTreatmentPlanId';

import type { AppDispatch, RootState } from '../../../../store';
import type { HeaderMetadataState, HeaderViewModel } from '../types';

export function useHeaderViewModel(): HeaderViewModel {
  const isMobile = useMediaQuery('(max-width:480px)');
  const isDesktop = useMediaQuery('(min-width:600px)');
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation(['header', 'invoice', 'common']);
  const { pathname } = useLocation();
  const { user, getAccessTokenSilently } = useAuth0();
  const sessionNotesV2Enabled = useBooleanFlagValue(
    FeatureFlags.SESSION_NOTES_V2_ENABLED,
    false,
  );
  const { current: session } = useSelector((state: RootState) => state.sessions);
  const { user: userState } = useSelector((state: RootState) => state.auth);
  const { current: claim } = useSelector((state: RootState) => state.claims);
  const { provider, loading: providerLoading } = useSelector((state: RootState) => state.providers);
  const { current: invoiceDetails } = useSelector((state: RootState) => state.claimsInvoice);
  const { current: clientAssessment } = useSelector((state: RootState) => state.clientAssessment);
  const { current: clientSupervision } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );
  const { current: treatmentPlan, tpStatusIdByLabel } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { current: parentSupervision } = useSelector((state: RootState) => state.parentSupervision);
  const { current: groupParentSN } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { current: btSupervision } = useSelector((state: RootState) => state.btSupervision);
  const { onboardingQuestionnaireDetails } = useSelector(
    (state: RootState) => state.onboardingQuestionnaire,
  );
  const { intakeQuestionnaireDetails } = useSelector(
    (state: RootState) => state.intakeQuestionnaire,
  );
  const selectedClientActiveTreatmentPlanId = useSelectedClientActiveTreatmentPlanId();
  const title = useMemo(() => getHeaderTitleForPath(t, pathname), [pathname, t]);
  const isSessionNotesV2Session =
    sessionNotesV2Enabled && session?.note_version === SESSION_NOTE_VERSIONS.V2;
  const treatmentPlanStatus = useMemo(
    () => getTreatmentPlanStatus(treatmentPlan, tpStatusIdByLabel),
    [treatmentPlan, tpStatusIdByLabel],
  );

  useEffect(() => {
    if (
      providerLoading ||
      !provider?.id ||
      !provider?.preferred_language ||
      !areUserRolesIn(userState?.roles, [Roles.Guardian])
    ) {
      return;
    }

    const sessionPreferredLanguage = sessionStorage.getItem(LOOKUP_SESSION_STORAGE);
    const isPreferredLanguageLoaded =
      sessionStorage.getItem('preferred_language_loaded') === 'true';

    if (i18n.resolvedLanguage === sessionPreferredLanguage && isPreferredLanguageLoaded) {
      return;
    }

    if (sessionPreferredLanguage && isPreferredLanguageLoaded) {
      void i18n.changeLanguage(sessionPreferredLanguage);
    } else if (i18n.resolvedLanguage !== provider.preferred_language) {
      void i18n.changeLanguage(provider.preferred_language);
    }

    sessionStorage.setItem('preferred_language_loaded', 'true');
  }, [i18n, provider, providerLoading, userState?.roles]);

  useEffect(() => {
    dispatch(providerActions.getProvider({ getAccessTokenSilently }));
  }, [dispatch, getAccessTokenSilently]);

  useEffect(() => {
    if (!areUserRolesIn(userState?.roles, [Roles.Prbt, Roles.Vbt])) return;
    dispatch(sessionActions.getUnacknowledgedSessions({ getAccessTokenSilently }));
  }, [dispatch, getAccessTokenSilently, pathname, userState?.roles]);

  const metadata = useMemo<HeaderMetadataState>(() => {
    const isNewSessionNotesPage =
      title === getHeaderPageTitle(t, 'sessionNotes') && pathname.includes('/new-session');

    if (clientSupervision?.id && title === getHeaderPageTitle(t, 'clientSupervisionNotes')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: clientSupervision.id,
        pageIdPrefix: ENTITY_PREFIX.ClientSupervisionNotes,
        status: clientSupervision.status || '',
        author: getHeaderAuthorName(
          clientSupervision.bcba?.first_name,
          clientSupervision.bcba?.last_name,
        ),
        bay: clientSupervision.bay || '',
      };
    }

    if (
      session?.id &&
      (title === getHeaderPageTitle(t, 'sessionNotes') ||
        title === getHeaderPageTitle(t, 'sessionNotesReview'))
    ) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: session.id,
        pageIdPrefix: ENTITY_PREFIX.SessionNotes,
        status: getHeaderSessionStatusLabel(t, session.status),
        author: getHeaderAuthorName(
          session.session_provider?.first_name,
          session.session_provider?.last_name,
        ),
        headerTreatmentPlanId:
          isSessionNotesV2Session ? (session.treatment_plan_id ?? null) : null,
        bay: session.bay || '',
      };
    }

    if (isNewSessionNotesPage) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        status: 'New',
        author:
          getHeaderAuthorName(provider?.first_name, provider?.last_name) ||
          getHeaderAuthorName(userState?.first_name, userState?.last_name) ||
          user?.name ||
          user?.email ||
          '',
        headerTreatmentPlanId: sessionNotesV2Enabled ? selectedClientActiveTreatmentPlanId : null,
      };
    }

    if (clientAssessment?.id && title === getHeaderPageTitle(t, 'assessmentNotes')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: clientAssessment.id,
        pageIdPrefix: ENTITY_PREFIX.ClientAssessmentNote,
        status: clientAssessment.status || '',
        author: getHeaderAuthorName(
          clientAssessment.provider?.first_name,
          clientAssessment.provider?.last_name,
        ),
        bay: clientAssessment.bay || '',
      };
    }

    if (treatmentPlan?.id && title === getHeaderPageTitle(t, 'treatmentPlan')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: treatmentPlan.id,
        pageIdPrefix: ENTITY_PREFIX.TreatmentPlan,
        status: treatmentPlanStatus.text,
        author: getHeaderAuthorName(
          treatmentPlan.provider?.first_name,
          treatmentPlan.provider?.last_name,
        ),
      };
    }

    if (
      onboardingQuestionnaireDetails?.id &&
      title === getHeaderPageTitle(t, 'onboardingQuestionnaire')
    ) {
      const onboardingMetadata = getQuestionnaireHeaderMetadata(onboardingQuestionnaireDetails);

      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: onboardingQuestionnaireDetails.id,
        pageIdPrefix: ENTITY_PREFIX.OnboardingQuestionnaire,
        author: onboardingMetadata.author,
        created: onboardingMetadata.created,
        child: onboardingMetadata.child,
      };
    }

    if (
      intakeQuestionnaireDetails?.id &&
      title === getHeaderPageTitle(t, 'intakeQuestionnaire')
    ) {
      const intakeMetadata = getQuestionnaireHeaderMetadata(intakeQuestionnaireDetails);

      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: intakeQuestionnaireDetails.id,
        pageIdPrefix: ENTITY_PREFIX.IntakeQuestionnaire,
        author: intakeMetadata.author,
        created: intakeMetadata.created,
        child: intakeMetadata.child,
      };
    }

    if (parentSupervision?.id && title === getHeaderPageTitle(t, 'parentSupervisionNotes')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: parentSupervision.id,
        pageIdPrefix: ENTITY_PREFIX.ParentSupervisionNotes,
        status: parentSupervision.status || '',
        author: getHeaderAuthorName(
          parentSupervision.bcba?.first_name,
          parentSupervision.bcba?.last_name,
        ),
        bay: parentSupervision.bay || '',
      };
    }

    if (groupParentSN?.id && title === getHeaderPageTitle(t, 'groupSupervisionNotes')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: groupParentSN.id,
        pageIdPrefix: ENTITY_PREFIX.GroupSupervisionNotes,
        status: groupParentSN.status || '',
        author: getHeaderAuthorName(groupParentSN.bcba?.first_name, groupParentSN.bcba?.last_name),
      };
    }

    if (btSupervision?.id && title === getHeaderPageTitle(t, 'btSupervisionNotes')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: btSupervision.id,
        pageIdPrefix: ENTITY_PREFIX.BTSupervisionNotes,
        status: btSupervision.status || '',
        author: getHeaderAuthorName(btSupervision.bcba?.first_name, btSupervision.bcba?.last_name),
      };
    }

    if (claim?.id && title === getHeaderPageTitle(t, 'billingClaim')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: claim.id,
        pageIdPrefix: ENTITY_PREFIX.Claim,
        status: claim.status || '',
        author: getHeaderAuthorName(claim.bcba?.first_name, claim.bcba?.last_name),
      };
    }

    if (invoiceDetails?.id && title === getHeaderPageTitle(t, 'invoiceInfo')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: invoiceDetails.id,
        pageIdPrefix: ENTITY_PREFIX.Invoice,
        status: getHeaderInvoiceStatusLabel(t, invoiceDetails.status),
        author: getHeaderAuthorName(
          invoiceDetails.biller?.first_name,
          invoiceDetails.biller?.last_name,
        ),
      };
    }

    if (invoiceDetails?.id && title === getHeaderPageTitle(t, 'invoiceDetail')) {
      return {
        ...EMPTY_HEADER_METADATA,
        title,
        pageId: invoiceDetails.id,
        pageIdPrefix: ENTITY_PREFIX.Invoice,
      };
    }

    return {
      ...EMPTY_HEADER_METADATA,
      title,
    };
  }, [
    btSupervision,
    claim,
    clientAssessment,
    clientSupervision,
    groupParentSN,
    intakeQuestionnaireDetails,
    invoiceDetails,
    isSessionNotesV2Session,
    onboardingQuestionnaireDetails,
    parentSupervision,
    pathname,
    provider,
    selectedClientActiveTreatmentPlanId,
    session,
    t,
    title,
    treatmentPlan,
    treatmentPlanStatus.text,
    user?.email,
    user?.name,
    userState?.first_name,
    userState?.last_name,
  ]);

  const showHeaderMetadata =
    isDesktop &&
    (Boolean(metadata.pageId) ||
      Boolean(
        metadata.author ||
          metadata.status ||
          metadata.created ||
          metadata.child ||
          metadata.bay ||
          metadata.headerTreatmentPlanId,
      ));

  const isTreatmentPlanV3Header =
    Boolean(treatmentPlan?.id) &&
    treatmentPlan?.version === TREATMENT_PLAN_V3_VERSION &&
    title === getHeaderPageTitle(t, 'treatmentPlan');

  return {
    ...metadata,
    i18n,
    isDesktop,
    isMobile,
    isTreatmentPlanV3Header,
    pathname,
    provider,
    providerLoading,
    showHeaderMetadata,
    showQuestionnaireHeader:
      title === getHeaderPageTitle(t, 'onboardingQuestionnaire') ||
      title === getHeaderPageTitle(t, 'intakeQuestionnaire'),
    t,
    user,
    userState,
  };
}
